import type { PDFDocumentProxy } from "pdfjs-dist";

/**
 * pdf.js, loaded on demand (the code screen never needs it), with its worker set up the Vite way.
 * Tests replace this module, since jsdom has no canvas.
 */
type PdfJs = typeof import("pdfjs-dist");

let lib: Promise<PdfJs> | null = null;

export function pdfjs(): Promise<PdfJs> {
  lib ??= Promise.all([import("pdfjs-dist"), import("pdfjs-dist/build/pdf.worker.min.mjs?url")]).then(([m, worker]) => {
    m.GlobalWorkerOptions.workerSrc = worker.default;
    return m;
  });
  return lib;
}

const docs = new Map<string, Promise<PDFDocumentProxy>>();

/** Where pdf.js finds the standard 14 fonts (served by the Vite plugin in vite.config.ts). */
const FONTS = `${import.meta.env.BASE_URL}pdfjs/standard_fonts/`;

/** A static demo file that isn't there comes back as the app's HTML page; say so before pdf.js tries to parse it. */
async function assertPdf(url: string): Promise<void> {
  const res = await fetch(url, { method: "HEAD" });
  const type = res.headers.get("Content-Type") ?? "";
  if (!res.ok || !/pdf|octet-stream/i.test(type)) throw new Error(`Not a PDF: ${url}`);
}

/**
 * One loaded document per URL, shared by every viewer that shows it. A failure isn't cached.
 * `checkType` (demo files) checks the response is a PDF first.
 */
export function loadPdf(url: string, withCredentials: boolean, checkType = false): Promise<PDFDocumentProxy> {
  const key = `${withCredentials ? "c" : "a"}|${url}`;
  let p = docs.get(key);
  if (!p) {
    p = (checkType ? assertPdf(url) : Promise.resolve()).then(() =>
      pdfjs().then(
        (m) =>
          m.getDocument({
            url,
            withCredentials,
            enableXfa: false,
            standardFontDataUrl: new URL(FONTS, window.location.href).href,
          }).promise,
      ),
    );
    p.catch(() => docs.delete(key));
    docs.set(key, p);
  }
  return p;
}

/** Annotations off: the page's own content only. Form widgets would draw their own (sometimes stale) values; we draw ours. */
export async function annotationModeNone(): Promise<number> {
  return (await pdfjs()).AnnotationMode.DISABLE;
}

export async function textLayerClass() {
  return (await pdfjs()).TextLayer;
}
